/**
 * El PIN d'un operari: alfabet, format, generació i com s'ensenya (OBR-25).
 *
 * El PIN és la credencial de planta: 7 caràcters d'un alfabet de 32 símbols, únic a tot Obrava,
 * generat pel sistema i mostrat UNA sola vegada. Aquest mòdul és la font de veritat del seu
 * FORMAT; el servidor (`pin-login`, `tenant-pin-manage`) i el teclat de planta fan servir les
 * mateixes funcions, així que un PIN que aquí és vàlid ho és a tot arreu. Pur: sense I/O, sense
 * rellotge i sense aleatorietat implícita (la font d'aleatorietat entra per paràmetre).
 *
 * L'ALFABET
 * `ABCDEFGHJKLMNPQRSTUVWXYZ23456789`: les 26 lletres i els 10 dígits MENYS `0`, `O`, `1` i `I`,
 * que a mà, amb guants i sobre una targeta impresa, es confonen entre ells. Són 32 = 2⁵ símbols,
 * de manera que cada símbol són exactament 5 bits (vegeu {@link generatePin}), i 32⁷ =
 * 34.359.738.368 PINs possibles. NO hi ha cap normalització que converteixi un `0` en una `O` o un
 * `1` en una `I`: els quatre exclosos no són mai vàlids, ni escrits ni en cap altra forma.
 */

import type { AppLanguage } from '../enums/index.js';

/** Els 32 símbols d'un PIN: les lletres i els dígits sense `0`, `O`, `1` ni `I`. */
export const PIN_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Quants caràcters té un PIN. */
export const PIN_LENGTH = 7;

/** Quants PINs diferents hi ha: 32⁷ = 34.359.738.368. */
export const PIN_SPACE = PIN_ALPHABET.length ** PIN_LENGTH;

/** Els caràcters que `normalizePin` treu: espais de qualsevol mena i tots els guionets. */
const PIN_SEPARATORS = /[\s‐-―-]+/g;

/**
 * Deixa un PIN escrit o enganxat en la forma que es compara: sense espais ni guionets
 * («k7m 4qxz», «K7M-4QXZ») i en majúscules. Amb això qui el teclegi a mà o l'enganxi de la targeta
 * l'escriu com vulgui. Els espais dels extrems també hi són (és el «trim»).
 *
 * Només es posen en majúscula les lletres ASCII: un `toUpperCase()` complet convertiria una `ß`
 * en `SS` i un PIN invàlid acabaria passant per vàlid. Cap altra lletra es toca, i `0`, `O`, `1` i
 * `I` es queden com són: no es «corregeixen» mai.
 */
export function normalizePin(raw: string): string {
  return raw.replace(PIN_SEPARATORS, '').replace(/[a-z]+/g, (letters) => letters.toUpperCase());
}

/**
 * Si `pin` té la forma d'un PIN: exactament {@link PIN_LENGTH} caràcters, tots de l'alfabet.
 * Estricte a posta (ni minúscules ni guionets): qui rep un text escrit per una persona el passa
 * abans per {@link normalizePin}.
 */
export function isValidPinFormat(pin: string): boolean {
  if (pin.length !== PIN_LENGTH) return false;
  for (const symbol of pin) {
    if (!PIN_ALPHABET.includes(symbol)) return false;
  }
  return true;
}

/**
 * Un PIN nou a partir de bytes aleatoris que posa qui crida: els {@link PIN_LENGTH} primers, un
 * per símbol, amb `byte & 31`.
 *
 * SENSE BIAIX: 256 és múltiple de 32, així que cada símbol surt de exactament 8 dels 256 valors
 * possibles d'un byte i tots són igual de probables; no cal descartar cap valor (el «rejection
 * sampling» que caldria amb un alfabet de, per exemple, 30 símbols). Això només és cert mentre
 * l'alfabet tingui 32 símbols, per això el test ho comprova.
 *
 * Els bytes han de venir d'un generador criptogràfic (`crypto.getRandomValues` al servidor);
 * aquesta funció no en tria cap. Els bytes de més s'ignoren.
 *
 * @throws RangeError si hi ha menys de {@link PIN_LENGTH} bytes.
 */
export function generatePin(randomBytes: Uint8Array): string {
  if (randomBytes.length < PIN_LENGTH) {
    throw new RangeError(
      `generatePin: need at least ${String(PIN_LENGTH)} random bytes, got ${String(randomBytes.length)}`,
    );
  }
  let pin = '';
  for (let i = 0; i < PIN_LENGTH; i += 1) {
    // `& 31` són els 5 bits baixos: un índex de 0 a 31 sobre l'alfabet.
    pin += PIN_ALPHABET.charAt((randomBytes[i] ?? 0) & 31);
  }
  return pin;
}

/** Quants caràcters té el primer grup d'un PIN quan s'ensenya («K7M-4QXZ»): tres, i el segon en porta la resta (quatre). */
const PIN_DISPLAY_GROUP = Math.floor(PIN_LENGTH / 2);

/**
 * El PIN com es llegeix a la pantalla de l'administrador i a la targeta: un grup de tres i un de
 * quatre amb un guionet, «K7M-4QXZ». Accepta el PIN en qualsevol forma que {@link normalizePin} entengui i la
 * desfà amb ella: `normalizePin(formatPinForDisplay(pin)) === pin`. Un text de menys de quatre
 * caràcters no té guionet i un de més llarg no es retalla: això només dona format, no valida.
 */
export function formatPinForDisplay(pin: string): string {
  const normalized = normalizePin(pin);
  if (normalized.length <= PIN_DISPLAY_GROUP) return normalized;
  return `${normalized.slice(0, PIN_DISPLAY_GROUP)}-${normalized.slice(PIN_DISPLAY_GROUP)}`;
}

/**
 * «Operari» en cada idioma: el nom que rep un PIN quan l'administrador no n'escriu cap. Són els
 * mateixos valors que `domain.roles.operator` dels catàlegs (un test ho vigila); viuen aquí i no
 * s'importen d'`i18n` perquè el domini no depèn dels catàlegs, igual que els dies de la setmana
 * de `supplierOrdering`.
 */
const OPERATOR_LABELS: Readonly<Record<AppLanguage, string>> = {
  ca: 'Operari',
  es: 'Operario',
  en: 'Operator',
};

/**
 * El nom per defecte d'un PIN sense nom: «Operari 3». El número `n` el tria qui crida (el
 * següent d'aquell obrador), no pas aquest mòdul. El nom d'un operari és únic dins l'obrador, així
 * que qui crida ha de comprovar que no hi sigui ja; mai s'hi afegeix cap sufix en silenci.
 *
 * @throws RangeError si `n` no és un enter positiu.
 */
export function defaultOperatorLabel(n: number, language: AppLanguage): string {
  if (!Number.isInteger(n) || n < 1) {
    throw new RangeError(`defaultOperatorLabel: n must be a positive integer, got ${String(n)}`);
  }
  return `${OPERATOR_LABELS[language] ?? OPERATOR_LABELS.ca} ${String(n)}`;
}
